import React from 'react'
import { Link } from 'react-router-dom'
import { Image } from '@deal/components'
import { withAnalytics, WithAnalyticsProps } from '#src/containers/Analytics'
import { formatMonetaryAmountAsJsx } from '#src/services/money'
import { IdentityConsumer } from '#src/containers/Identity'
import { getSellableUrl } from '#src/services/sellable'
import SaveSellable from '#src/components/SaveSellable'
import { SellableCardFragment } from '#src/generated/graphql'
import YachtBathroomsIcon from './yacht-bathrooms.svg'
import YachtCabinsIcon from './yacht-cabins.svg'
import YachtCapacityIcon from './yacht-capacity.svg'
import classnames from 'classnames'
import styles from './styles.css'
import { getSellableEcommerceProperties } from '#src/services/tracking'

interface ExperienceCardProps extends React.HTMLAttributes<HTMLDivElement> {
  sellable: SellableCardFragment
  position?: number
  lazyLoad?: boolean
}

class ExperienceCard extends React.Component<ExperienceCardProps & WithAnalyticsProps> {
  public constructor(props: ExperienceCardProps & WithAnalyticsProps) {
    super(props)

    this.handleClick = this.handleClick.bind(this)
    this.getSellableUrl = this.getSellableUrl.bind(this)
  }

  public getSellableUrl() {
    const { sellable } = this.props

    let targetSellable = sellable.variationMatrix
      ? sellable.variationMatrix.defaultVariant
      : sellable

    return getSellableUrl(targetSellable)
  }

  public handleClick() {
    const sellable = this.props.sellable

    if (this.props.analytics) {
      this.props.analytics.events.productClicked({
        ...getSellableEcommerceProperties(sellable),
        position: this.props.position
      })
    }
  }

  public render() {
    const { sellable, className, lazyLoad } = this.props

    return (
      <div className={classnames(styles.card, className)}>
        <Link to={this.getSellableUrl} onClick={this.handleClick}>
          <IdentityConsumer>
            {({ myself }) => (
              <>
                <div className={styles.header}>
                  {myself && (
                    <SaveSellable
                      sellable={sellable}
                      iconClassName={styles.saveIcon}
                      containerClassName={styles.saveContainer}
                    />
                  )}
                  <div className={styles.carousel}>
                    {sellable.images[0] && (
                      <div className={styles.imageContainer}>
                        <Image
                          className={styles.image}
                          lazyLoad={lazyLoad}
                          src={sellable.images[0].url}
                          width={694}
                          height={347}
                          imgixParams={{ fit: 'fill' }}
                          htmlAttributes={{
                            alt: sellable.title
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <div className={styles.caption}>
                    {sellable.serviceLocation && sellable.serviceLocation.displayTitle && (
                      <div className={styles.location}>
                        {sellable.serviceLocation.displayTitle.toUpperCase()}
                      </div>
                    )}
                    <div className={styles.title}>{sellable.title}</div>
                  </div>
                </div>
                <div className={styles.footer}>
                  <div className={styles.attributes}>
                    {sellable.charterCapacity && sellable.charterCapacity.formatted && (
                      <div className={styles.attribute}>
                        <YachtCapacityIcon className={styles.attributeIcon} />
                        <span
                          dangerouslySetInnerHTML={{
                            __html: sellable.charterCapacity.formatted.displayHtml
                          }}
                        />
                      </div>
                    )}
                    {sellable.charterCabins && sellable.charterCabins.formatted && (
                      <div className={styles.attribute}>
                        <YachtCabinsIcon className={styles.attributeIcon} />
                        <span
                          dangerouslySetInnerHTML={{
                            __html: sellable.charterCabins.formatted.displayHtml
                          }}
                        />
                      </div>
                    )}
                    {sellable.charterBathrooms && sellable.charterBathrooms.formatted && (
                      <div className={styles.attribute}>
                        <YachtBathroomsIcon className={styles.attributeIcon} />
                        <span
                          dangerouslySetInnerHTML={{
                            __html: sellable.charterBathrooms.formatted.displayHtml
                          }}
                        />
                      </div>
                    )}
                  </div>
                  {sellable.variationMatrix && sellable.variationMatrix.priceRange && (
                    <div className={styles.pricing}>
                      from{' '}
                      <em className={styles.total}>
                        {formatMonetaryAmountAsJsx(sellable.variationMatrix.priceRange!.min, {
                          round: 'floor'
                        })}
                      </em>
                    </div>
                  )}
                </div>
              </>
            )}
          </IdentityConsumer>
        </Link>
      </div>
    )
  }
}

export default withAnalytics(ExperienceCard)
